import { IPaginatedRepository, IRepository } from '@/repositories/types'
import {
  useQuery,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query'

/**
 * @file Defines a generic hook for fetching data using TanStack Query.
 * @summary This hook abstracts the data-fetching logic, providing a reusable and consistent
 * way to query data from any repository that implements the IRepository or IPaginatedRepository interface.
 */

/**
 * A generic hook for fetching data.
 *
 * @template T - The type of the data being fetched.
 * @template TError - The type of the error that might be thrown.
 * @param {object} params - The parameters for the hook.
 * @param {IRepository<T, any, any> | IPaginatedRepository<T, any, any>} params.repository - The repository instance to use for fetching data.
 * @param {(repo: any) => Promise<any>} params.fetcher - A function that calls the repository's fetch method.
 * @param {UseQueryOptions<any, TError>} [params.options] - Optional TanStack Query options.
 * @returns {UseQueryResult<any, TError>} The result of the useQuery hook.
 */
export const useData = <T, TError = Error>({
  repository,
  fetcher,
  queryKey,
  options,
}: {
  repository: IRepository<T, any, any> | IPaginatedRepository<T, any, any>
  fetcher: (
    repo: IRepository<T, any, any> | IPaginatedRepository<T, any, any>
  ) => Promise<T | T[] | { items: T[]; count: number } | null>
  queryKey: (string | number | null | undefined)[]
  options?: Omit<
    UseQueryOptions<T | T[] | { items: T[]; count: number } | null, TError>,
    'queryKey' | 'queryFn'
  >
}): UseQueryResult<T | T[] | { items: T[]; count: number } | null, TError> => {
  return useQuery<T | T[] | { items: T[]; count: number } | null, TError>({
    queryKey: [...queryKey, repository],
    queryFn: () => fetcher(repository),
    ...options,
  })
}
